import { supabase } from "@/integrations/supabase/client";

/** Метки источника регистрации (UTM / referrer). ?ref= не трогаем — это реферал. */
export const SIGNUP_SOURCE_KEY = "td_signup_source";

export type SignupSource = { source: string | null; medium: string | null; campaign: string | null };

const clean = (v: string | null | undefined) => {
  const t = (v ?? "").trim().slice(0, 200);
  return t || null;
};

/** Чистая логика: метки из адреса, иначе hostname внешнего referrer. */
export function parseSignupSource(href: string, referrer: string): SignupSource | null {
  try {
    const url = new URL(href);
    const p = url.searchParams;
    const utm: SignupSource = {
      source: clean(p.get("utm_source")),
      medium: clean(p.get("utm_medium")),
      campaign: clean(p.get("utm_campaign")),
    };
    if (utm.source || utm.medium || utm.campaign) return utm;
    if (referrer) {
      const host = new URL(referrer).hostname;
      if (host && host !== url.hostname) return { source: clean(host), medium: "referrer", campaign: null };
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Сохраняет первый источник один раз; уже сохранённый не перезаписывается. */
export function captureSignupSource(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(SIGNUP_SOURCE_KEY)) return;
    const s = parseSignupSource(window.location.href, document.referrer);
    if (s) window.localStorage.setItem(SIGNUP_SOURCE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function getStoredSignupSource(): SignupSource | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SIGNUP_SOURCE_KEY);
    return raw ? (JSON.parse(raw) as SignupSource) : null;
  } catch {
    return null;
  }
}

function clearStoredSignupSource(): void {
  try {
    window.localStorage.removeItem(SIGNUP_SOURCE_KEY);
  } catch {
    /* ignore */
  }
}

/** Пишет метки в профиль один раз, только если колонки пустые; затем очищает. */
export async function applyStoredSignupSource(userId: string): Promise<void> {
  const s = getStoredSignupSource();
  if (!s) return;
  const { data, error } = await supabase
    .from("profiles")
    .select("signup_source, signup_medium, signup_campaign")
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) return;
  if (data.signup_source || data.signup_medium || data.signup_campaign) {
    clearStoredSignupSource();
    return;
  }
  const { error: updErr } = await supabase
    .from("profiles")
    .update({ signup_source: s.source, signup_medium: s.medium, signup_campaign: s.campaign })
    .eq("user_id", userId)
    .is("signup_source", null)
    .is("signup_medium", null)
    .is("signup_campaign", null);
  if (!updErr) clearStoredSignupSource();
}
